import Core            from '@/core/Core';
import BudgetPrice     from '@/domains/BudgetPrices/BudgetPrice';
import BudgetPricesDAO from '@/domains/BudgetPrices/BudgetPricesDAO';
import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import type DateSimple from "@/core/types/DateSimple";
import Product from "@/domains/Products/Product";

/**
 * Сервис домена бюджетных цен.
 */
class BudgetPricesService {

  /**
   * Загрузить бюджетную цену по productId, действующую на дату (без даты — на сегодня).
   */
  static async get(productId: number, date?: DateSimple): Promise<BudgetPrice | null> {
    const response = await BudgetPricesDAO.get(productId, date);
    return Core.utils.hydrate(response.data, BudgetPrice.fromServer);
  }

  /**
   * Все записи бюджетной цены по номенклатуре — прошлые, действующая и будущие.
   */
  static async getHistory(productId: number): Promise<BudgetPrice[]> {
    const response = await BudgetPricesDAO.getHistory(productId);
    return Core.utils.hydrateArray(response.data, BudgetPrice.fromServer);
  }

  /**
   * Загрузить номенклатуру с бюджетными ценами — действующей и будущими (Product.budgetPrices).
   * @param productIds — один или несколько productId; без параметра вернёт всю номенклатуру с ценами
   */
  static async getList(productIds?: number | number[]): Promise<Product[]> {
    const response = await BudgetPricesDAO.getList({ productIds });

    return Core.utils.hydrateArray(response.data, Product.fromServer);
  }

  static async getListPaginated(
    params?: Parameters<typeof BudgetPricesDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort
    }
  ): Promise<{ data: Product[], pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await BudgetPricesDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Product.fromServer),
      pagination: response.meta.pagination
    };
  }

  /**
   * Новая запись бюджетной цены. Возвращает false, если условия совпали с действующими и запись не создана.
   */
  static async add(budgetPrice: BudgetPrice): Promise<boolean> {
    const response = await BudgetPricesDAO.add(budgetPrice);

    if (response.ok === true && response.data === true) {
      Core.showNotification.success('Бюджетная цена сохранена');
      return true;
    } else {
      return false;
    }
  }

  /**
   * Удалить запись бюджетной цены.
   * Возвращает true, только если запись действительно была удалена.
   */
  static async delete(budgetPrice: BudgetPrice): Promise<boolean> {
    const response = await BudgetPricesDAO.delete(budgetPrice);

    if (response.ok === true && response.data === true) {
      Core.showNotification.success('Бюджетная цена удалена');
      return true;
    } else {
      return false;
    }
  }

  /** Удаление бюджетной цены с подтверждением */
  static async deleteWithConfirm(budgetPrice: BudgetPrice): Promise<boolean> {
    const confirmed = await Core.confirm('Подтверждаете удаление бюджетной цены с ' + budgetPrice.validityPeriod.start?.format() + '?');

    if (confirmed === true) {
      const isDeleted = await this.delete(budgetPrice);

      if (isDeleted === true) {
        budgetPrice.isDeleted = true;
      }

      return isDeleted;
    }
    else {
      return false;
    }
  }

  /**
   * Показывает карточку новой записи бюджетной цены. Записи не редактируются — карточка всегда создаёт новую,
   * условия копирует из действующей цены номенклатуры. null — номенклатуру выбирают в карточке.
   * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
   */
  static showBudgetPriceCard(product: Product | null): Promise<boolean> {
    return Core.dialogs.BudgetPriceCard.showAsPromise({ product });
  }

  /**
   * Показывает историю записей бюджетной цены номенклатуры.
   */
  static showHistory(product: Product): Promise<boolean> {
    return Core.dialogs.BudgetPricesHistoryDialog.showAsPromise({ product });
  }

  /** Меню строки реестра — строка реестра это номенклатура с ценами */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Добавить цену',
        'add',
        (product: Product) => { return this.showBudgetPriceCard(product); }
      ),
      new ContextMenuItem(
        'История бюджетной цены',
        'history',
        (product: Product) => { return this.showHistory(product); }
      )
    ]
  }

  /** Меню отдельной записи цены (виджет, история) */
  static get recordContextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Удалить',
        'delete',
        (budgetPrice: BudgetPrice) => { return this.deleteWithConfirm(budgetPrice); }
      )
    ]
  }
}

export default BudgetPricesService;